import { createFeatureSelector, createSelector } from '@ngrx/store';
import { MULTI_CONTRACT_MAX_SELECTION_ALLOWED } from '../../../constants/default.const';
import { ContractFilterStatus } from '../../../enums/contract-filter-status.enum';
import { ContractCP } from '../../../models/contract-model';
import { isCreditContract } from '../../../utils/contract-utils';
import { selectRouteParamsTotal } from '../../app-wide/config/config.selectors';
import { ContractState, contractsFeatureKey } from './contract.reducers';

export const contractSelector = createFeatureSelector<ContractState>(contractsFeatureKey);

export const getPageNumber = createSelector(contractSelector, (state: ContractState) => state.pageNumber);

export const getContracts = createSelector(contractSelector, (state: ContractState) => state.contracts.filter(contract => contract.latestToShow));

export const getNoMoreRecord = createSelector(contractSelector, (state: ContractState) => state.noMoreRecord);

export const getSelected = createSelector(contractSelector, (state: ContractState) => state.selectedContractId);

export const getSelectedContracts = createSelector(contractSelector, (state: ContractState) => state.selectedContracts);

export const getPaymentOpen = createSelector(contractSelector, (state: ContractState) => state.paymentOpen);

export const getSelectedContractsModels = createSelector(contractSelector, getSelectedContracts, (state: ContractState, selectedContracts: string[]) =>
    state.contracts.filter((contract: ContractCP) => selectedContracts.includes(contract.contractId))
);

export const getContractsTotal = createSelector(contractSelector, getSelectedContracts, selectRouteParamsTotal, (state: ContractState, selectedContracts: string[]) => {
    let total = 0;

    // Always calculate sum from contracts (ignore routeParamTotal)
    // For single contracts with partial payment, the amount is validated and set
    // in onSetMultiboundaries$ effect (payment.effects.ts:202-227)
    // For multiple contracts, always use full sum of AmountDue
    state.contracts
        .filter((contract: ContractCP) => selectedContracts.includes(contract.contractId))
        .forEach((contract: ContractCP) => {
            if (isCreditContract(contract)) {
                total -= Math.abs(Number(contract.AmountDue));
            } else {
                total += Number(contract.AmountDue);
            }
        });

    return Math.round((total + Number.EPSILON) * 100) / 100;
});

export const getCreditContractsTotal = createSelector(contractSelector, getSelectedContracts, (state: ContractState, selectedContracts: string[]) => {
    let total = 0;

    state.contracts
        .filter((contract: ContractCP) => selectedContracts.includes(contract.contractId))
        .forEach((contract: ContractCP) => {
            if (isCreditContract(contract)) {
                total -= Math.abs(Number(contract.AmountDue));
            }
        });

    return Math.round((total + Number.EPSILON) * 100) / 100;
});

export const getSelectedContractsTotal = createSelector(contractSelector, getSelectedContracts, (state: ContractState, selectedContracts: string[]) => {
    let total = 0;

    state.contracts
        .filter((contract: ContractCP) => selectedContracts.includes(contract.contractId))
        .forEach((contract: ContractCP) => {
            if (!isCreditContract(contract)) {
                total += Number(contract.AmountDue);
            }
        });

    return Math.round((total + Number.EPSILON) * 100) / 100;
});

export const getSelectedCreditContracts = createSelector(contractSelector, getSelectedContracts, (state: ContractState, selectedContracts: string[]) =>
    state.contracts.filter((contract: ContractCP) => selectedContracts.includes(contract.contractId) && isCreditContract(contract))
);

export const getSelectedNonCreditContracts = createSelector(contractSelector, getSelectedContracts, (state: ContractState, selectedContracts: string[]) =>
    state.contracts.filter((contract: ContractCP) => selectedContracts.includes(contract.contractId) && !isCreditContract(contract))
);

export const isContractSelected = (contractId: string) =>
    createSelector(getSelectedContracts, (selectedContracts: string[]) => {
        return selectedContracts.includes(contractId);
    });

export const isContractSelectable = (contractId: string) =>
    createSelector(contractSelector, getSelectedContracts, isContractSelected(contractId), (state: ContractState, selectedContracts: string[], isSelected: boolean) => {
        const selectingContract = state.contracts.find((contract: ContractCP) => contract.contractId === contractId);

        // Allow already selected contracts to remain selectable
        if (isSelected) {
            return true;
        }
        if (selectedContracts.length === MULTI_CONTRACT_MAX_SELECTION_ALLOWED) {
            return false;
        }
        if (Number(selectingContract?.AmountDue) === 0) {
            return false;
        }

        /**
         * For Elite, Returned == Closed
         */
        if (selectingContract?.status !== ContractFilterStatus.Returned) {
            return false;
        }

        if (selectingContract?.paymentPending) {
            return false;
        }

        return true;
    });

export const selectContractById = (contractId: string) => createSelector(getContracts, (contracts: ContractCP[]) => contracts.find(contract => contract.contractId === contractId));

export const selectTotalPaidByContractId = (contractId: string) =>
    createSelector(selectContractById(contractId), (contract: ContractCP | undefined) => {
        if (contract) {
            const grandTotal: string | undefined = contract.GrandTotal;
            const amountDue = contract.AmountDue;

            if (typeof grandTotal !== 'number' || typeof amountDue !== 'number') {
                return '';
            }

            const totalPaid: number = grandTotal - amountDue;
            return totalPaid;
        }
        return '';
    });

export const selectContractError = createSelector(contractSelector, (state: ContractState) => state.error);

export const selectContractDetail = createSelector(contractSelector, getSelected, (state: ContractState, selectedContractId) => {
    if (selectedContractId && state.contractDetail && state.contractDetail.Id === selectedContractId) {
        return state.contractDetail;
    }
    return null;
});

export const isMaxContractSelected = createSelector(getSelectedContracts, (selectedContracts: string[]) => {
    return selectedContracts.length === MULTI_CONTRACT_MAX_SELECTION_ALLOWED;
});

/**
 * Validate contracts for multi-payment eligibility
 * Business rules: Status = Returned, AmountDue > 0, no pending payment
 * @param contractIds Array of contract IDs to validate
 */
export const areContractsValidForMultiPayment = (contractIds: string[]) =>
    createSelector(getContracts, (contracts: ContractCP[]) => {
        if (contractIds.length <= 1) {
            return { valid: true, invalidContracts: [], invalidCount: 0 };
        }

        const selectedContracts = contracts.filter(c => contractIds.includes(c.contractId));
        const invalid = selectedContracts.filter(c => c.status !== ContractFilterStatus.Returned || Number(c.AmountDue) === 0 || c.paymentPending);

        return {
            valid: invalid.length === 0,
            invalidContracts: invalid,
            invalidCount: invalid.length
        };
    });

/**
 * Calculate total AmountDue for specific contracts
 * @param contractIds Array of contract IDs
 */
export const getTotalAmountDueForContracts = (contractIds: string[]) =>
    createSelector(getContracts, (contracts: ContractCP[]) => {
        const selectedContracts = contracts.filter(c => contractIds.includes(c.contractId));
        const total = selectedContracts.reduce((sum, c) => sum + Number(c.AmountDue), 0);
        return Math.round((total + Number.EPSILON) * 100) / 100;
    });

/**
 * Validate payment amount matches total AmountDue
 * Business rule: Multi-contract requires full payment (no partial)
 * @param contractIds Array of contract IDs
 * @param providedAmount Amount from routeParams (optional)
 */
export const validatePaymentAmount = (contractIds: string[], providedAmount?: number) =>
    createSelector(getTotalAmountDueForContracts(contractIds), (totalDue: number) => {
        if (contractIds.length <= 1) {
            return { valid: true };
        }

        if (providedAmount === undefined || providedAmount === null) {
            return { valid: true };
        }

        const rounded = Math.round((providedAmount + Number.EPSILON) * 100) / 100;
        return {
            valid: rounded === totalDue,
            providedAmount: rounded,
            requiredAmount: totalDue
        };
    });

export const selectContractHasPaymentHistory = createSelector(selectContractDetail, state => {
    if (state?.Payment && state?.Payment.length > 0) {
        return true;
    }
    return false;
});

export const selectContractLineItems = createSelector(selectContractDetail, state => state?.LineItems);

export const getTrackedItems = createSelector(contractSelector, (state: ContractState) => state.trackedItems);

export const getQuoteConversionInProgress = createSelector(contractSelector, (state: ContractState) => state.quoteConversionInProgress);
